import { Component } from '@theme/component';

/**
 * @typedef {Object} ShareRefs
 * @property {HTMLDivElement} actions
 * @property {HTMLButtonElement} shareButton
 * @property {HTMLButtonElement} copyButton
 * @property {HTMLDetailsElement} manual
 * @property {HTMLInputElement} link
 * @property {HTMLParagraphElement} status
 * @extends {Component<ShareRefs>}
 */
class MobShare extends Component {
  requiredRefs = ['actions', 'shareButton', 'copyButton', 'manual', 'link', 'status'];
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#prepare();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepare();
  }

  #prepare() {
    this.#setBusy(this.#busy);
  }

  #url() {
    const savedCart = this.closest('[data-mob-save-cart]')?.querySelector('a[href]');
    const url = new URL(savedCart?.href || this.dataset.url);
    const originalVariant = url.searchParams.get('variant');
    url.search = '';
    url.hash = '';
    if (this.dataset.kind === 'product') {
      const input = this.closest('.buy-buttons-block')?.querySelector('input[name="id"]');
      const currentPage = new URL(window.location.href);
      const currentVariant = currentPage.pathname === url.pathname ? currentPage.searchParams.get('variant') : null;
      const variant = currentVariant || (input instanceof HTMLInputElement ? input.value : originalVariant);
      if (variant && /^\d+$/.test(variant)) url.searchParams.set('variant', variant);
    } else {
      url.searchParams.set('storefront', 'true');
    }
    this.refs.link.value = url.href;
    return url.href;
  }

  #setBusy(value) {
    this.#busy = value;
    this.refs.shareButton.disabled = value;
    this.refs.copyButton.disabled = value;
  }

  async share() {
    if (this.#busy) return;
    const url = this.#url();
    this.refs.status.textContent = '';
    this.#setBusy(true);
    try {
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: this.dataset.title, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      await this.#copyUrl(url);
    } finally {
      this.#setBusy(false);
    }
  }

  async copy() {
    if (this.#busy) return;
    const url = this.#url();
    this.refs.status.textContent = '';
    this.#setBusy(true);
    try {
      await this.#copyUrl(url);
    } finally {
      this.#setBusy(false);
    }
  }

  async #copyUrl(url) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      if (this.isConnected) this.refs.status.textContent = this.dataset.copied;
    } catch {
      if (!this.isConnected) return;
      this.refs.manual.open = true;
      this.refs.status.textContent = this.dataset.manual;
      this.selectLink();
    }
  }

  selectLink() {
    this.refs.link.focus();
    this.refs.link.select();
    this.refs.link.setSelectionRange(0, this.refs.link.value.length);
  }
}

if (!customElements.get('mob-share')) customElements.define('mob-share', MobShare);
